import { getValidationErrors } from '@/Shared/ValidationErrorDirective';
import { AxiosResponse, AxiosRequestConfig } from 'axios';
import { Dictionary } from 'vue-router/types/router';
import mixins from 'vue-typed-mixins';
import VatsAPI from '../VatsAPI';
import DeletePromptMixin from './DeletePromptMixin';
import { AcceptsPayload, getRequestMethod, isUrlConfig, NoPayload, UrlOptions } from './UrlConfig';
import constants from '@/constants';
import fileSaver from 'file-saver';

function buildEntityListComponentMixin<T extends { recID: number }>() {
    return mixins(DeletePromptMixin).extend({
        beforeMount() {
            this.loadEntities();
        },
        data() {
            return {
                entities: [] as T[],
                loading: false,
                validationErrors: {} as Dictionary<string[]>,
                editItem: null as T | null,
                loadingEntity: {} as { [recId: number]: boolean },
                i18nPrefix: '',
            };
        },
        computed: {
            entityUrls(): UrlOptions {
                return '';
            }
        },
        methods: {
            afterLoad(response: AxiosResponse<T[]>): T[] {
                return response.data;
            },
            afterSave(response: AxiosResponse<T>): T {
                return response.data;
            },
            // tslint:disable
            afterDelete(response: AxiosResponse<void>): void {
            },
            // tslint:enable
            async loadEntities() {
                await this.internalLoad<T[]>(response => {
                    response.data = this.afterLoad(response);
                    this.entities = response.data;
                }, undefined);
            },
            async saveEntity(entity: T) {
                const { method, url } = (isUrlConfig(this.entityUrls)
                    ? {
                        method: getRequestMethod<AcceptsPayload<T>>(this.entityUrls.update.urlAction),
                        url: this.entityUrls.update.url
                    }
                    : { method: VatsAPI.post, url: this.entityUrls });
                try {
                    this.$set(this.loadingEntity, entity.recID, true);
                    const response = await method(url, entity);
                    response.data = this.afterSave(response);
                    const index = this.entities.findIndex(item => item.recID === entity.recID);
                    this.entities.splice(index, 1, response.data);

                    this.editItem = null;
                }
                catch (e) {
                    this.validationErrors = getValidationErrors(e.response);
                    const translatedTitle = this.$i18n.t(`${this.i18nPrefix}.saveFailedTitle`);
                    const translatedMessage = this.$i18n.t(`${this.i18nPrefix}.saveFailedMessage`);
                    this.$bvToast.toast(translatedMessage.toString(), {
                        title: translatedTitle.toString(),
                        autoHideDelay: 5000
                    });
                    throw e;
                }
                finally {
                    this.$set(this.loadingEntity, entity.recID, false);
                }
            },
            async deleteEntity(entity: T) {
                if (entity.recID) {
                    if (!(await this.promptDelete())) {
                        return;
                    }

                    try {
                        this.$set(this.loadingEntity, entity.recID, true);
                        const { method, url } = isUrlConfig(this.entityUrls)
                            ? {
                                method: getRequestMethod(this.entityUrls.delete.urlAction),
                                url: this.entityUrls.delete.url
                            }
                            : {
                                method: (endpoint: string, data: T) => VatsAPI.delete(endpoint, {
                                    data,
                                    headers: {
                                        ['content-type']: 'application/json'
                                    }
                                }),
                                url: this.entityUrls
                            };
                        const response = await method(url, entity);
                        this.afterDelete(response);
                    }
                    catch (e) {
                        const displayDefault = this.onDeleteError(e);
                        if (displayDefault === false) {
                            return;
                        }

                        const translatedTitle =
                            this.$i18n.t(`${this.i18nPrefix}.deleteFailedTitle`);
                        const translatedMessage =
                            this.$i18n.t(`${this.i18nPrefix}.deleteFailedMessage`);
                        this.$bvToast.toast(translatedMessage.toString(), {
                            title: translatedTitle.toString(),
                            autoHideDelay: 5000
                        });
                        // Early return; don't want to splice out if delete failed
                        return;
                    }
                    finally {
                        this.$set(this.loadingEntity, entity.recID, false);
                    }
                }

                const index = this.entities.findIndex(item => item.recID === entity.recID);
                this.entities.splice(index, 1);
            },
            async internalLoad<Result>(
                onSuccess: (results: AxiosResponse<Result>) => any,
                config?: AxiosRequestConfig | undefined
                ) {
                const { method, url } = isUrlConfig(this.entityUrls)
                    ? {
                        method: getRequestMethod<NoPayload<Result>>(this.entityUrls.get.urlAction),
                        url: this.entityUrls.get.url
                    }
                    : { method: VatsAPI.get, url: this.entityUrls };
                try {
                    this.loading = true;
                    const response = (await method<Result>(url, config));
                    onSuccess(response);
                }
                catch (e) {
                    const translatedTitle = this.$i18n.t(`${this.i18nPrefix}.loadFailedTitle`);
                    const translatedMessage = this.$i18n.t(`${this.i18nPrefix}.loadFailedMessage`);
                    this.$bvToast.toast(translatedMessage.toString(), {
                        title: translatedTitle.toString(),
                        autoHideDelay: 5000
                    });
                    throw e;
                }
                finally {
                    this.loading = false;
                }
            },
            async downloadExcelSheet() {
                await this.internalLoad<Blob>(response => {
                    fileSaver.saveAs(response.data, 'ListContents.xlsx');
                },
                {
                    headers: {
                        accept: constants.ExcelMimeType
                    },
                    responseType: 'blob'
                });
            },
            cancelEdit() {
                if (this.editItem && !this.editItem.recID) {
                    const index = this.entities.findIndex(item => item.recID === this.editItem!.recID);
                    this.entities.splice(index, 1);
                }
                this.editItem = null;
            },
            edit(mapping: T) {
                this.cancelEdit();
                this.editItem = Object.assign({}, mapping);
            },
            add() {
                if (this.editItem && !this.editItem.recID) {
                    return;
                }
                const newMap = {} as T;
                this.entities.splice(0, 0, newMap);
                this.edit({} as T);
            },
            locationChanged() {
                this.validationErrors = {};
                this.editItem = null;
                this.loadEntities();
            },
            onDeleteError(e: any | undefined): boolean {
                return false;
            },
        }
    });
}

export default buildEntityListComponentMixin;
